import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Building, MapPin, Send, HelpCircle, Users, User, Briefcase, Globe, MessageSquare, CheckCircle, AlertTriangle, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Hardcoding the production URL to ensure it's correct
const BACKEND_URL = "https://sbg-backend.onrender.com";

const EmployerContact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyEmail: "",
    companyName: "",
    jobTitle: "",
    zipCode: "",
    companyPhone: "",
    country: "",
    contactPreference: "",
    helpType: "",
    staffCount: "",
    laborType: "",
    specializations: "",
    message: "",
    terms: false,
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setFormStatus({
      submitted: false,
      success: false,
      message: "",
    });

    try {
      // Using the new dedicated endpoint
      const endpoint = "/api/employer-contact";
      console.log("Submitting employer contact to URL:", BACKEND_URL + endpoint);
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => 
          ({ message: `Server returned ${response.status}: ${response.statusText}` })
        );
        throw new Error(errorData.message || 'Failed to submit form');
      }

      setFormStatus({
        submitted: true,
        success: true,
        message: t('employerContact.form.success'),
      });
      setFormData({
        firstName: "",
        lastName: "",
        companyEmail: "",
        companyName: "",
        jobTitle: "",
        zipCode: "",
        companyPhone: "",
        country: "",
        contactPreference: "",
        helpType: "",
        staffCount: "",
        laborType: "",
        specializations: "",
        message: "",
        terms: false,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error.message);
      setFormStatus({
        submitted: true,
        success: false,
        message: t('employerContact.form.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-accent/5">
      <main className="flex-grow p-8 max-w-7xl mx-auto w-full">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">
            {t('employerContact.title')}
          </h1>
          <p className="text-xl text-accent-700 mb-4">
            {t('employerContact.subtitle')}
          </p>
          <div className="inline-flex space-x-4">
            <Link
              to="/employer-contact"
              className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition-colors inline-flex items-center shadow-lg hover:shadow-xl group"
            >
              <Users className="mr-2 group-hover:scale-110 transition-transform" />
              {t('contact.forEmployers')}
            </Link>
            <Link
              to="/contact"
              className="bg-accent text-white px-6 py-3 rounded-lg hover:bg-accent-dark transition-colors inline-flex items-center shadow-lg hover:shadow-xl group"
            >
              <HelpCircle className="mr-2 group-hover:scale-110 transition-transform" />
              {t('contact.generalInquiries')}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center mb-6">
              <Building className="w-8 h-8 text-primary mr-4" />
              <h2 className="text-2xl font-semibold text-gray-900">
                {t('employerContact.employerServices.title')}
              </h2>
            </div>
            <p className="text-accent-700 mb-8 leading-relaxed">
              {t('employerContact.employerServices.description')}
            </p>
            <ul className="space-y-6">
              <li className="flex items-start group">
                <div className="bg-primary-light p-2 rounded-full mr-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <Users className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 text-lg">
                    {t('employerContact.employerServices.services.directHire')}
                  </h3>
                </div>
              </li>
              <li className="flex items-start group">
                <div className="bg-primary-light p-2 rounded-full mr-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <Users className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 text-lg">
                    {t('employerContact.employerServices.services.temporary')}
                  </h3>
                </div>
              </li>
              <li className="flex items-start group">
                <div className="bg-primary-light p-2 rounded-full mr-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <Users className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 text-lg">
                    {t('employerContact.employerServices.services.contractToHire')}
                  </h3>
                </div>
              </li>
              <li className="flex items-start group">
                <div className="bg-primary-light p-2 rounded-full mr-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <Users className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 text-lg">
                    {t('employerContact.employerServices.services.staffAugmentation')}
                  </h3>
                </div>
              </li>
            </ul>
            
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Office</h3>
              <div className="space-y-3 text-gray-700">
                <p className="flex items-center">
                  <MapPin className="h-5 w-5 text-primary mr-2" />
                  <span>4600 East Washington Street<br />Suite 300<br />Phoenix, AZ 85034</span>
                </p>
                <p className="flex items-center">
                  <Phone className="h-5 w-5 text-primary mr-2" />
                  <span>602-400-8028</span>
                </p>
                <p className="flex items-center">
                  <Phone className="h-5 w-5 text-primary mr-2" />
                  <span>Fax: 623-321-1327</span>
                </p>
                <p className="flex items-center">
                  <Mail className="h-5 w-5 text-primary mr-2" />
                  <a href="mailto:info@scottbiz.net" className="text-primary hover:underline">info@scottbiz.net</a>
                </p>
              </div>
              
              <div className="mt-6 h-64 rounded-lg overflow-hidden shadow-md">
                <MapContainer 
                  center={[33.4484, -112.0740]} 
                  zoom={14} 
                  style={{ height: '100%', width: '100%' }}
                  className="z-0"
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker position={[33.4484, -112.0740]}>
                    <Popup>
                      Scott Business Group<br />
                      4600 East Washington Street<br />
                      Suite 300<br />
                      Phoenix, AZ 85034
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
            {formStatus.submitted ? (
              <div
                className={`p-6 rounded-lg flex items-center ${
                  formStatus.success ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
                }`}
              >
                {formStatus.success ? (
                  <CheckCircle className="w-6 h-6 mr-3 text-green-500" />
                ) : (
                  <AlertTriangle className="w-6 h-6 mr-3 text-red-500" />
                )}
                <span>{formStatus.message}</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('employerContact.form.firstName')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('employerContact.form.lastName')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="companyEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('employerContact.form.companyEmail')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="companyEmail"
                      name="companyEmail"
                      value={formData.companyEmail}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('employerContact.form.companyName')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('employerContact.form.jobTitle')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Briefcase className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="jobTitle"
                      name="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('employerContact.form.zipCode')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPin className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('employerContact.form.companyPhone')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        id="companyPhone"
                        name="companyPhone"
                        value={formData.companyPhone}
                        onChange={handleInputChange}
                        className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('employerContact.form.country')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Globe className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                      required
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('employerContact.form.contactPreference.label')}
                  </label>
                  <div className="flex space-x-6">
                    <label className="inline-flex items-center hover:text-primary transition-colors cursor-pointer">
                      <input
                        type="radio"
                        name="contactPreference"
                        value="email"
                        checked={formData.contactPreference === "email"}
                        onChange={handleInputChange}
                        className="form-radio text-primary h-5 w-5"
                      />
                      <span className="ml-2 flex items-center">
                        <Mail className="h-4 w-4 mr-1 text-primary" />
                        {t('employerContact.form.contactPreference.email')}
                      </span>
                    </label>
                    <label className="inline-flex items-center hover:text-primary transition-colors cursor-pointer">
                      <input
                        type="radio"
                        name="contactPreference"
                        value="phone"
                        checked={formData.contactPreference === "phone"}
                        onChange={handleInputChange}
                        className="form-radio text-primary h-5 w-5"
                      />
                      <span className="ml-2 flex items-center">
                        <Phone className="h-4 w-4 mr-1 text-primary" />
                        {t('employerContact.form.contactPreference.phone')}
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="helpType" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('employerContact.form.helpType.label')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <HelpCircle className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="helpType"
                      name="helpType"
                      value={formData.helpType}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm appearance-none bg-no-repeat"
                      style={{ backgroundPosition: 'right 0.5rem center', backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%236b7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3e%3c/svg%3e")' }}
                    >
                      <option value="" disabled>Select help type...</option>
                      <option value="staffing">{t('employerContact.form.helpType.staffing')}</option>
                      <option value="information">{t('employerContact.form.helpType.information')}</option>
                      <option value="other">{t('employerContact.form.helpType.other')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="staffCount" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('employerContact.form.staffCount')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Users className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="staffCount"
                      name="staffCount"
                      value={formData.staffCount}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="laborType" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('employerContact.form.laborType')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Briefcase className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="laborType"
                      name="laborType"
                      value={formData.laborType}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="specializations" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('employerContact.form.specializations')}
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                      <FileText className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      id="specializations"
                      name="specializations"
                      value={formData.specializations}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                    ></textarea>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('employerContact.form.message')}
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                      <MessageSquare className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full pl-10 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary transition shadow-sm"
                      placeholder="Please provide any additional details about your staffing needs..."
                    ></textarea>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-sm">
                  <label className="flex items-start cursor-pointer group">
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        name="terms"
                        checked={formData.terms}
                        onChange={handleInputChange}
                        className="form-checkbox h-5 w-5 text-primary focus:ring-primary rounded border-gray-300 transition-all group-hover:border-primary"
                        required
                      />
                    </div>
                    <span className="ml-3 text-sm text-gray-700">
                      {t('employerContact.form.terms')}{' '}
                      <Link to="/terms" className="text-primary hover:text-primary-dark underline font-medium">
                        {t('common.termsAndConditions')}
                      </Link>{' '}
                      {t('common.and')}{' '}
                      <Link to="/privacy" className="text-primary hover:text-primary-dark underline font-medium">
                        {t('common.privacyPolicy')}
                      </Link>.
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full ${
                    isSubmitting ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                  } text-white px-6 py-3 rounded-md transition-colors flex items-center justify-center font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t('employerContact.form.submitting')}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      {t('employerContact.form.submit')}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployerContact;
